import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import CartPage from './pages/CartPage';
import Checkout from './components/Checkout';
import BkashPaymentPage from './pages/BkashPaymentPage';
import AdminPanel from './admin/AdminPanel';
import CategoriesManager from './admin/CategoriesManager';
import ProductsManager from './admin/ProductsManager';
import ShowcaseManager from './admin/ShowcaseManager';
import OrdersManager from './admin/OrdersManager';
import AdminLogin from './admin/AdminLogin';
import PaymentsManager from './admin/PaymentsManager';
import './App.css';
import TouchSparkle from './components/TouchSparkle';
import GenieCursor from './components/GenieCursor';

function AppContent() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  const handleCategorySelect = (category, subcategory) => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    navigate('/products');
  };

  const handleResetFilter = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    navigate('/');
  };

  const handleAddToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    
    showNotification('Item added to cart');
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const handleRemoveFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
    showNotification('Item removed from cart');
  };

  const handleOrderComplete = () => {
    setCart([]);
  };

  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const showNotification = (message) => {
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  };

  return (
    <div className="App">
      <Navbar
        onCategorySelect={handleCategorySelect}
        onResetFilter={handleResetFilter}
        cartCount={getCartCount()}
      />
      <Routes>
        <Route path="/" element={<HomePage onCategorySelect={handleCategorySelect} />} />
        <Route 
          path="/products" 
          element={
            <ProductsPage 
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              onAddToCart={handleAddToCart}
            />
          } 
        />
        <Route 
          path="/product/:id" 
          element={<ProductDetailsPage onAddToCart={handleAddToCart} />} 
        />
        <Route 
          path="/cart" 
          element={
            <CartPage 
              cart={cart}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveFromCart={handleRemoveFromCart}
            />
          } 
        />
        <Route
          path="/checkout"
          element={
            <Checkout
              cart={cart}
              onOrderComplete={handleOrderComplete}
            />
          }
        />
        <Route path="/bkash-payment" element={<BkashPaymentPage />} />
      </Routes>
      <Footer onShowNotification={showNotification} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <TouchSparkle />
      <GenieCursor />
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/*" element={<AdminRoutes />} />
        
        {/* Public Routes */}
        <Route path="/*" element={<PublicRoutes />} />
      </Routes>
    </Router>
  );
}

function AdminRoutes() {
  const [stats, setStats] = useState({ categories: 0, products: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const [categoriesRes, productsRes] = await Promise.all([
        fetch('http://localhost:8000/api/categories', { headers }),
        fetch('http://localhost:8000/api/products', { headers })
      ]);
      
      const categoriesData = await categoriesRes.json();
      const productsData = await productsRes.json();
      
      setStats({
        categories: categoriesData.count || 0,
        products: productsData.total || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Check authentication on every route change
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token && location.pathname !== '/admin/login') {
      navigate('/admin/login');
    }
  }, [location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/" element={<AdminPanel stats={stats} fetchStats={fetchStats} />} />
      <Route path="/categories" element={<CategoriesManager onUpdate={fetchStats} />} />
      <Route path="/products" element={<ProductsManager onUpdate={fetchStats} />} />
      <Route path="/showcase" element={<ShowcaseManager />} />
      <Route path="/orders" element={<OrdersManager />} />
      <Route path="/payments" element={<PaymentsManager />} />
    </Routes>
  );
}

function PublicRoutes() {
  return <AppContent />;
}

export default App;
